import { motion } from "framer-motion";
import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  Command,
  Hexagon,
  Triangle,
  Circle,
  Box,
  PenTool,
  Search,
  Database,
  Target,
  BarChart3,
  Rocket,
  CheckCircle2,
  Star,
  Sparkles,
  Paperclip,
  X,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CountUp from "@/components/CountUp";
import ServiceDetailModal from "@/components/ServiceDetailModal";
import NewsletterSection from "@/components/NewsletterSection";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const SERVICES = [
  {
    key: "content-marketing",
    icon: PenTool,
    title: "Content Marketing",
    tagline: "Narrative-led demand.",
    desc: "Editorial content engineered for pipeline. Thought leadership, long-form assets, and distribution built around ICP intent signals.",
    span: "md:col-span-8",
    metric: "+312% organic engagement",
    image: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1400&q=80",
    overview:
      "We build content programs that compound. Not blog volume — narrative infrastructure that ranks, converts, and gives your sales team something meaningful to reference in every deal. Every asset is written by senior operators, mapped to a stage in your buying committee's journey, and instrumented for attribution.",
    process: [
      { title: "ICP + intent research", detail: "We map your buying committee, decode their category vocabulary, and identify the intent signals worth investing behind." },
      { title: "Editorial thesis", detail: "A quarterly narrative arc — pillar topics, opinion pieces, and category-defining assets that ladder to pipeline outcomes." },
      { title: "Production sprints", detail: "Weekly cadence with senior writers. Interviews with your subject-matter experts, edited and shipped inside a defined SLA." },
      { title: "Distribution engine", detail: "Owned, earned, and paid distribution across LinkedIn, newsletters, syndication, and sales enablement." },
      { title: "Attribution + iteration", detail: "Weekly performance review against pipeline. Double down on winners, retire underperformers." },
    ],
    deliverables: [
      "6–10 long-form assets / quarter",
      "Executive ghostwriting (LinkedIn + newsletters)",
      "Sales enablement one-pagers",
      "Attribution dashboard + monthly report",
    ],
  },
  {
    key: "seo-marketing",
    icon: Search,
    title: "SEO Marketing",
    tagline: "Compound traffic advantage.",
    desc: "Technical SEO, topical authority, and programmatic SERP capture for B2B categories.",
    span: "md:col-span-4",
    metric: "+4.8x organic MQLs",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=80",
    overview:
      "SEO for B2B categories where every keyword maps to a buying stage. We combine technical hygiene, topical authority builds, and programmatic coverage of comparison and integration terms — the queries your buyers actually run before they ever fill in a demo form.",
    process: [
      { title: "Technical audit", detail: "Crawl budget, Core Web Vitals, indexation, schema, internal linking — fix what's leaking authority before we scale content." },
      { title: "Category topic map", detail: "Cluster your category into pillar topics + supporting content. Map every URL to a stage of the funnel." },
      { title: "Authority builds", detail: "Digital PR, expert citations, and outreach to publications your buyers read." },
      { title: "Programmatic capture", detail: "Comparison pages, integration pages, alternatives pages — coverage for every high-intent SERP." },
      { title: "Rank + revenue reporting", detail: "SERP position tied to attributed pipeline, not vanity impressions." },
    ],
    deliverables: [
      "Full technical audit + remediation",
      "Topic cluster + editorial calendar",
      "Backlink acquisition (8–15 / month)",
      "Programmatic page templates",
      "Monthly ranking + revenue report",
    ],
  },
  {
    key: "lead-enrichment",
    icon: Database,
    title: "Lead Enrichment",
    tagline: "Turn lists into pipeline.",
    desc: "Firmographic + technographic + intent data appended to your CRM records — deduped, validated, and routed.",
    span: "md:col-span-4",
    metric: "92% match accuracy",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=80",
    overview:
      "Your CRM is only as good as the data inside it. We enrich every record with firmographic, technographic, and intent signals — then dedupe, normalize, and route it so your reps only touch accounts that fit the ICP and are actively in-market.",
    process: [
      { title: "ICP scoring model", detail: "We define what a good-fit account looks like — firmographic, technographic, engagement, and intent inputs." },
      { title: "Enrichment stack setup", detail: "Wire Clearbit / Apollo / 6sense / Bombora into your CRM with our tested field mappings." },
      { title: "Dedupe + normalize", detail: "Clean existing records. Standardize company names, job titles, and industry taxonomies." },
      { title: "Routing rules", detail: "Round-robin, territory, or account-based routing tied to your ICP score." },
      { title: "Ongoing hygiene", detail: "Automated freshness checks, decayed-data alerts, and quarterly re-scoring." },
    ],
    deliverables: [
      "ICP scoring model in CRM",
      "Enrichment tools integrated + mapped",
      "One-time dedupe + normalization",
      "Routing playbook + monitoring",
      "Quarterly data health review",
    ],
  },
  {
    key: "abm-marketing",
    icon: Target,
    title: "ABM Marketing",
    tagline: "Named-account precision.",
    desc: "Coordinated multi-channel plays across your top 100 accounts. Executive engagement mapped to buying committee stages.",
    span: "md:col-span-8",
    metric: "38% avg. account penetration",
    image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1400&q=80",
    overview:
      "ABM isn't a channel — it's a discipline. We build orchestrated plays across ads, outbound, content, and events for a defined set of named accounts. Every touch is tracked against the buying committee, so you know which stakeholders are heating up and when to trigger sales.",
    process: [
      { title: "Account selection", detail: "Tier 1 (deep custom), Tier 2 (personalized-scaled), Tier 3 (programmatic). We define account counts + resources per tier." },
      { title: "Buying committee mapping", detail: "Identify decision-makers, champions, blockers, and users. Enrich contact data across LinkedIn + intent signals." },
      { title: "Play design", detail: "Coordinated 60-90 day plays combining ads, direct mail, executive outreach, personalized content, and field events." },
      { title: "Orchestration + launch", detail: "Weekly stand-ups with sales. Trigger-based automation across marketing tech stack." },
      { title: "Account scoring + hand-off", detail: "Real-time account heat map. Sales activates when engagement crosses threshold." },
    ],
    deliverables: [
      "Tiered target account list",
      "Buying committee playbook per account",
      "Multi-channel campaign assets",
      "Ad campaigns (LinkedIn + display)",
      "Weekly reporting + heat map",
    ],
  },
  {
    key: "market-research",
    icon: BarChart3,
    title: "Market Research",
    tagline: "Certainty before commit.",
    desc: "Competitor benchmarking, buyer interviews, and category positioning research for high-consequence launches.",
    span: "md:col-span-6",
    metric: "22 interviews · avg cycle",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1400&q=80",
    overview:
      "Before you spend $2M on a launch or reposition your category, get certainty. We run structured qualitative research — buyer interviews, win/loss analysis, and competitor teardowns — to give you defensible positioning grounded in real market evidence.",
    process: [
      { title: "Research brief", detail: "Define the strategic decision this research must inform. Hypotheses, exclusions, and success criteria." },
      { title: "Recruit + screen", detail: "Recruit 15-25 target buyers via LinkedIn, our network, or your existing CRM. Screen for fit." },
      { title: "1:1 interviews", detail: "60-minute semi-structured interviews conducted by senior researchers. Recorded + transcribed." },
      { title: "Synthesis workshop", detail: "Coded findings, tension mapping, jobs-to-be-done framework. Delivered as a synthesis workshop with your team." },
      { title: "Positioning brief", detail: "Written narrative: category frame, differentiator, proof points, messaging pillars, and next-quarter GTM implications." },
    ],
    deliverables: [
      "15–25 buyer interviews (recorded)",
      "Competitor teardown (top 5)",
      "Win/loss synthesis",
      "Positioning brief + narrative",
      "Live synthesis workshop",
    ],
  },
  {
    key: "sales-acceleration",
    icon: Rocket,
    title: "Sales Acceleration",
    tagline: "From meeting → close.",
    desc: "Playbooks, sequences, and enablement content designed to compress your sales cycle by 40%.",
    span: "md:col-span-6",
    metric: "-41% cycle time",
    image: "https://images.unsplash.com/photo-1552581234-26160f608093?auto=format&fit=crop&w=1400&q=80",
    overview:
      "Marketing generates the meeting. Sales acceleration wins it. We embed with your revenue team to rebuild the mid-funnel — outbound sequences, discovery playbooks, objection libraries, and stage-gated enablement — so your reps spend less time in deals and close more of them.",
    process: [
      { title: "Cycle audit", detail: "Analyze last 90 days of closed-won + closed-lost deals. Identify stage friction, drop-offs, and common objections." },
      { title: "Playbook build", detail: "Discovery framework, MEDDPICC / SPICED qualification, objection library, and stage-exit criteria." },
      { title: "Sequence design", detail: "Multi-touch outbound sequences (email + LinkedIn + phone) mapped to persona + intent stage." },
      { title: "Enablement content", detail: "Battlecards, ROI calculators, one-pagers, and video microsites for each deal stage." },
      { title: "Coaching + iteration", detail: "Weekly deal reviews. Rep coaching. Iteration based on win/loss data." },
    ],
    deliverables: [
      "Cycle audit report",
      "Discovery + qualification playbook",
      "Outbound sequences (4 personas)",
      "Battlecards + ROI calculator",
      "Weekly deal reviews (90 days)",
    ],
  },
];

const TRUST_ICONS = [Command, Hexagon, Triangle, Circle, Box];

const CLIENT_LOGOS = [
  { name: "AT&T", src: "https://cdn.simpleicons.org/atandt" },
  { name: "Dell", src: "https://cdn.simpleicons.org/dell" },
  { name: "Microsoft", src: "/logos/microsoft.svg", local: true },
  { name: "Red Hat", src: "https://cdn.simpleicons.org/redhat" },
  { name: "Adobe", src: "/logos/adobe.svg", local: true },
  { name: "HPE", src: "https://cdn.simpleicons.org/hp" },
  { name: "Cisco", src: "https://cdn.simpleicons.org/cisco" },
  { name: "Veeam", src: "https://cdn.simpleicons.org/veeam" },
];

const CASE_STUDIES = [
  {
    industry: "FinTech SaaS",
    result: "+287% SQLs",
    detail: "Series B FinTech scaled qualified pipeline from $2.1M to $8.1M in 9 months via ABM + Content.",
  },
  {
    industry: "Cybersecurity",
    result: "$14.2M pipeline",
    detail: "Enterprise cyber platform captured 42 named accounts across FinServ vertical.",
  },
  {
    industry: "DevTools",
    result: "3.4x organic",
    detail: "Developer-first platform tripled organic traffic in two quarters via programmatic SEO.",
  },
];

const TESTIMONIALS = [
  {
    name: "Elena Marquez",
    role: "VP Marketing, Northlink Cloud",
    quote:
      "LeadTech Connect rebuilt our demand engine from the ground up. Six months in, our SQL-to-close rate doubled and we finally have a pipeline model we can forecast against.",
  },
  {
    name: "Marcus Chen",
    role: "Head of Growth, Fabric AI",
    quote:
      "Their ABM operators are the most disciplined we've worked with. They don't chase vanity metrics — every play maps back to revenue.",
  },
  {
    name: "Sarah Whitmore",
    role: "CRO, PolarStack",
    quote:
      "The content and SEO team compounds. We're now ranking for our category's most competitive terms with a fraction of the spend.",
  },
];

const STATS = [
  { value: "$142M+", label: "Pipeline generated" },
  { value: "180+", label: "B2B clients served" },
  { value: "3.8x", label: "Avg. pipeline ROI" },
  { value: "94%", label: "Client retention" },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Home() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "Content Marketing",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [openService, setOpenService] = useState(null);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File exceeds 15MB limit");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/lead-attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment(data);
      toast.success("File attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, attachment_id: attachment?.id || null });
      trackEvent("generate_lead", { service: form.service, has_attachment: !!attachment });
      toast.success("Thanks — a strategist will reach out within 24 hours.");
      setSubmitted(true);
      setForm({ name: "", email: "", company: "", phone: "", service: "Content Marketing", message: "" });
      setAttachment(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-24 overflow-hidden bg-white dark:bg-slate-950">
        <div className="absolute inset-0 grid-pattern opacity-60" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <motion.div
            className="md:col-span-7"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div data-testid="hero-eyebrow" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#0891B2] mb-6 border border-[#0891B2]/30 px-3 py-1 bg-[#0891B2]/5">
              <Sparkles size={12} /> B2B DEMAND ENGINEERING
            </div>
            <h1 data-testid="hero-heading" className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900 dark:text-white">
              We build revenue pipeline<br />
              <span className="text-slate-400 dark:text-slate-500">for</span> B2B teams that <span className="text-[#0891B2]">ship.</span>
            </h1>
            <p className="mt-6 max-w-xl text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
              Content, SEO, ABM, and sales acceleration — engineered by operators who have scaled pipeline at Series B+ SaaS. No agencies. No fluff. Just measurable pipeline.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#0056d3] text-white text-sm font-medium px-6 py-3 shadow-[0_4px_20px_rgba(0,122,255,0.25)] hover:shadow-[0_6px_28px_rgba(0,122,255,0.4)] transition-colors"
              >
                Book Free Consultation <ArrowRight size={16} />
              </a>
              <a
                href="#case-studies"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-slate-300 dark:border-slate-700 hover:border-slate-900 text-slate-900 dark:text-white text-sm px-6 py-3 hover:bg-slate-50 dark:bg-slate-900 transition-colors"
              >
                See Case Studies
              </a>
            </div>
          </motion.div>

          <motion.div
            className="md:col-span-5"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <div className="relative aspect-square border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xl">
              <img
                src="https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg"
                alt="Neon blue optical data fibers"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute bottom-6 left-6 right-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/95 backdrop-blur-xl p-4 shadow-lg">
                <div className="text-xs uppercase tracking-widest text-slate-500">Live Pipeline Signal</div>
                <div className="font-heading text-2xl mt-1 text-slate-900 dark:text-white">$4.2M <span className="text-[#0891B2] text-sm">↑ 38% QoQ</span></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.3em] text-slate-500">
              Trusted by B2B leaders across industries
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-8 gap-y-10 items-center">
            {CLIENT_LOGOS.map((c) => (
              <div
                key={c.name}
                data-testid={`client-logo-${c.name.toLowerCase().replace(/\s|&/g, "")}`}
                title={c.name}
                className={`flex items-center justify-center h-8 group ${c.local ? "text-slate-500 hover:text-slate-900 dark:text-white transition-colors" : ""}`}
              >
                <img
                  src={c.src}
                  alt={c.name}
                  className={`max-h-8 max-w-[130px] w-auto object-contain ${
                    c.local
                      ? "opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                      : "grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                  }`}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" className="py-24 md:py-32 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="max-w-3xl mb-16">
            <div className="text-xs uppercase tracking-[0.3em] text-[#0891B2] mb-4">Services</div>
            <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900 dark:text-white">
              Six revenue disciplines.<br />
              <span className="text-slate-400 dark:text-slate-500">One integrated system.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {SERVICES.map((s, idx) => {
              const Icon = s.icon;
              return (
                <motion.button
                  key={s.key}
                  type="button"
                  onClick={() => setOpenService(s)}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: idx * 0.05 }}
                  data-testid={`service-card-${s.key}`}
                  className={`tracing-card text-left bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-8 hover:border-slate-400 hover:shadow-lg transition-all group focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:ring-offset-2 ${s.span}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <Icon size={28} className="text-[#0891B2]" aria-hidden="true" />
                    <span className="text-xs font-mono text-slate-400 dark:text-slate-500">0{idx + 1}</span>
                  </div>
                  <h3 className="font-heading font-bold text-2xl md:text-3xl text-slate-900 dark:text-white mb-2 tracking-tight">
                    {s.title}
                  </h3>
                  <p className="text-slate-500 text-sm mb-4 italic">{s.tagline}</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed mb-6">{s.desc}</p>
                  <div className="border-t border-slate-200 dark:border-slate-800 pt-4 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest text-slate-500 group-hover:text-[#007AFF] transition-colors">
                      Learn more →
                    </span>
                    <span className="font-heading font-bold text-sm text-slate-900 dark:text-white">{s.metric}</span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: "easeOut" }}
              data-testid={`stat-${i}`}
            >
              <CountUp
                value={s.value}
                duration={2}
                className="font-heading font-black text-5xl md:text-6xl lg:text-7xl text-slate-900 dark:text-white tracking-tight leading-none block"
                accentClassName="text-[#0891B2]"
              />
              <div className="mt-4 text-xs uppercase tracking-[0.2em] text-slate-500">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ABOUT / APPROACH */}
      <section id="approach" className="py-24 md:py-32 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <motion.div {...fadeUp} className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-[#0891B2] mb-4">Our Approach</div>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-8 text-slate-900 dark:text-white">
              Operator-led. Data-obsessed.<br /><span className="text-slate-400 dark:text-slate-500">Zero fluff.</span>
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
              We're not a "brand agency." Every strategist on our team has scaled B2B pipeline as an in-house operator. We embed with your revenue org, wire attribution end-to-end, and treat every dollar of spend as an investment thesis.
            </p>
            <ul className="space-y-4">
              {[
                "Attribution-first: every play maps to pipeline",
                "Weekly reporting cadence tied to CRM data",
                "Senior operators only — no junior handoffs",
                "Fixed-fee engagements with revenue-based upside",
              ].map((line, i) => (
                <li key={i} data-testid={`approach-point-${i}`} className="flex items-start gap-3 text-sm text-slate-800 dark:text-slate-100">
                  <CheckCircle2 size={18} className="text-[#0891B2] mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="md:col-span-5">
            <div className="relative aspect-[4/5] border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1521791136064-7986c2920216"
                alt="B2B strategy meeting"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section id="case-studies" className="py-24 md:py-32 bg-slate-50 dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-16 max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[#0891B2] mb-4">Case Studies</div>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-[1.05] text-slate-900 dark:text-white">
              Signals over stories.
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CASE_STUDIES.map((c, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                data-testid={`case-study-${i}`}
                className="tracing-card bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-8 hover:border-slate-400 hover:shadow-lg transition-all"
              >
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-6">{c.industry}</div>
                <div className="font-heading font-black text-4xl md:text-5xl text-[#0891B2] tracking-tight mb-6">
                  {c.result}
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{c.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-24 md:py-32 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-16 max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[#0891B2] mb-4">Testimonials</div>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-[1.05] text-slate-900 dark:text-white">
              From the operators<br /><span className="text-slate-400 dark:text-slate-500">we work with.</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                data-testid={`testimonial-${i}`}
                className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-sm p-8 flex flex-col hover:shadow-lg transition-shadow"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} size={14} className="fill-[#0891B2] text-[#0891B2]" aria-hidden="true" />
                  ))}
                </div>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-sm flex-1">"{t.quote}"</p>
                <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-800">
                  <div className="font-heading font-bold text-slate-900 dark:text-white text-sm">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{t.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 md:py-32 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16">
          <motion.div {...fadeUp} className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#0891B2] mb-4">Contact</div>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-6 text-slate-900 dark:text-white">
              Let's talk pipeline.
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-8">
              Send us your context and we'll come back within 24 hours with an initial diagnostic — free, no commitment.
            </p>
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-slate-500 text-xs uppercase tracking-widest">Email</div>
                <div className="text-slate-900 dark:text-white">enquiry@leadtechconnect.com</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs uppercase tracking-widest">Phone</div>
                <div className="text-slate-900 dark:text-white">+1 (737) 862-1210</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs uppercase tracking-widest">HQ</div>
                <div className="text-slate-900 dark:text-white">Roscoe Blvd, Panorama City<br />Los Angeles, California 91402, US</div>
              </div>
            </div>
          </motion.div>

          <motion.form
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            onSubmit={submit}
            className="md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-sm p-8"
            data-testid="contact-form"
          >
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Name *</label>
              <input
                data-testid="contact-name"
                required
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Jane Doe"
                className="line-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Work Email *</label>
              <input
                data-testid="contact-email"
                required
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="jane@company.com"
                className="line-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Company *</label>
              <input
                data-testid="contact-company"
                required
                name="company"
                value={form.company}
                onChange={handleChange}
                placeholder="Acme Corp"
                className="line-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Phone</label>
              <input
                data-testid="contact-phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+1 (555) 000-0000"
                className="line-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-slate-500">Service Interest *</label>
              <select
                data-testid="contact-service"
                required
                name="service"
                value={form.service}
                onChange={handleChange}
                className="line-input appearance-none"
              >
                {SERVICES.map((s) => (
                  <option key={s.key} value={s.title} className="bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
                    {s.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-slate-500">How can we help? *</label>
              <textarea
                data-testid="contact-message"
                required
                name="message"
                value={form.message}
                onChange={handleChange}
                rows="4"
                placeholder="Tell us about your current pipeline and goals…"
                className="line-input resize-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-slate-500">Attachment (optional)</label>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                {!attachment ? (
                  <label
                    data-testid="contact-attachment-label"
                    className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700 hover:border-[#0891B2] hover:text-slate-900 dark:text-white px-4 py-2 cursor-pointer transition-colors"
                  >
                    <Paperclip size={14} />
                    {uploading ? "Uploading…" : "Attach brief / RFP"}
                    <input
                      data-testid="contact-attachment"
                      type="file"
                      onChange={handleFile}
                      disabled={uploading}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.png,.jpg,.jpeg,.webp"
                    />
                  </label>
                ) : (
                  <div className="inline-flex items-center gap-3 border border-[#0891B2]/50 bg-[#0891B2]/5 px-4 py-2 text-sm text-slate-900 dark:text-white">
                    <Paperclip size={14} className="text-[#0891B2]" />
                    <span className="truncate max-w-[240px]" data-testid="attachment-name">{attachment.filename}</span>
                    <button
                      type="button"
                      data-testid="attachment-remove"
                      onClick={() => setAttachment(null)}
                      aria-label="Remove attachment"
                      className="text-slate-500 hover:text-slate-900 dark:text-white"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                <span className="text-xs text-slate-500">PDF, DOC, image · max 15MB</span>
              </div>
            </div>
            <div className="md:col-span-2 pt-2">
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#0056d3] disabled:opacity-60 text-white text-sm font-medium px-6 py-3 shadow-[0_4px_20px_rgba(0,122,255,0.25)] hover:shadow-[0_6px_28px_rgba(0,122,255,0.4)] transition-colors"
              >
                {submitting ? "Sending…" : submitted ? "Sent — we'll be in touch" : "Send Message"} <ArrowRight size={16} />
              </button>
            </div>
          </motion.form>
        </div>
      </section>

      <NewsletterSection />
      <Footer />
      <ServiceDetailModal service={openService} onClose={() => setOpenService(null)} />
    </div>
  );
}
